import { useEffect, useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router'
import { authApi, AuthApiError } from '@/lib/auth-api'
import { useAuthStore } from '@/stores/auth.store'

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { user, setUser } = useAuthStore()

  const [isVerifying, setIsVerifying] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  const token = searchParams.get('token')

  useEffect(() => {
    if (token) {
      verifyEmail()
    } else {
      setIsVerifying(false)
      setError('No verification token provided')
    }
  }, [token])

  const verifyEmail = async () => {
    try {
      setIsVerifying(true)
      setError(null)

      const response = await authApi.verifyEmail(token!)

      // Update user in store if logged in
      if (response.user && user) {
        setUser(response.user)
      }

      setSuccess(true)

      // Redirect after a short delay
      setTimeout(() => {
        if (user?.role === 'parent') {
          navigate('/timeline')
        } else if (user?.role === 'childminder') {
          navigate('/dashboard')
        } else {
          navigate('/login')
        }
      }, 2000)
    } catch (err) {
      setError(err instanceof AuthApiError ? err.message : 'Verification failed')
    } finally {
      setIsVerifying(false)
    }
  }

  if (isVerifying) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent"></div>
          <p className="mt-2 text-gray-600">Verifying your email...</p>
        </div>
      </div>
    )
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-md">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900">Email Verified!</h1>
            <p className="mt-2 text-gray-600">
              Your email has been verified successfully.
            </p>
            <p className="mt-1 text-sm text-gray-500">Redirecting...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-md">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900">Verification Failed</h1>
          <p className="mt-2 text-gray-600">{error}</p>

          <div className="mt-4 space-y-2">
            {user ? (
              <Link
                to={user.role === 'parent' ? '/timeline' : '/dashboard'}
                className="block w-full rounded-lg bg-primary-600 py-2 text-center font-medium text-white hover:bg-primary-700"
              >
                Go to Dashboard
              </Link>
            ) : (
              <Link
                to="/login"
                className="block w-full rounded-lg bg-primary-600 py-2 text-center font-medium text-white hover:bg-primary-700"
              >
                Go to Login
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
